import React, { useEffect, useState } from 'react';
import { Calendar, Users, Sparkles, Clock, ArrowRight } from 'lucide-react';

const trackEvent = (name, params = {}) => {
  if (typeof window === 'undefined') return;
  if (window.gtag) window.gtag('event', name, params);
  if (window.fbq) window.fbq('track', name === 'lead_submit' ? 'Lead' : name, params);
};

const useCountdownToNextWebinar = () => {
  // Next webinar = next Saturday at 7pm IST (UTC+5:30) — compute in IST consistently for all viewers.
  const compute = () => {
    const nowUtc = Date.now();
    const istNow = new Date(nowUtc + 5.5 * 60 * 60 * 1000); // shift to IST
    const istTarget = new Date(istNow);
    const daysUntilSat = (6 - istNow.getUTCDay() + 7) % 7 || 7;
    istTarget.setUTCDate(istNow.getUTCDate() + daysUntilSat);
    istTarget.setUTCHours(19, 0, 0, 0);
    const diff = Math.max(0, istTarget.getTime() - istNow.getTime());
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return { d, h, m };
  };
  const [t, setT] = useState(compute());
  useEffect(() => {
    const i = setInterval(() => setT(compute()), 30000);
    return () => clearInterval(i);
  }, []);
  return t;
};

const MasterclassFunnel = () => {
  const { d, h, m } = useCountdownToNextWebinar();
  const [form, setForm] = useState({ name: '', phone: '', email: '', course: 'AI Marketing' });
  const [status, setStatus] = useState('idle');
  const seatsLeft = 47; // urgency anchor

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) return;
    setStatus('loading');
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: `Free AI Career Masterclass · Interested course: ${form.course}`,
          course: `Free Masterclass (${form.course})`,
          source: 'home-masterclass-funnel'
        })
      });
      if (!res.ok) throw new Error('submit failed');
      trackEvent('lead_submit', { source: 'masterclass-funnel', course: form.course });
      setStatus('ok');
    } catch (_) {
      setStatus('error');
    }
  };

  return (
    <section data-testid="masterclass-funnel" className="py-24 px-6 relative overflow-hidden" style={{ background: 'radial-gradient(ellipse at center, #14122e 0%, #08081a 60%, #000 100%)' }}>
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-[140px]"></div>
      <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-orange-500/15 rounded-full blur-[140px]"></div>

      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Left — emotional pitch */}
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-red-500/15 border border-red-500/30 rounded-full px-4 py-1.5 mb-5">
              <Sparkles className="w-4 h-4 text-red-300 animate-pulse" />
              <span className="text-xs font-bold text-red-200 uppercase tracking-widest">FREE Masterclass &middot; Limited Seats</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-5">
              Join Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-400">FREE AI Career Masterclass</span>
            </h2>
            <p className="text-base md:text-lg text-gray-300 mb-6 leading-relaxed">
              Learn how AI is transforming careers, freelancing &amp; business opportunities. Live session by industry trainers — &#x20B9;0 to attend.
            </p>

            <ul className="space-y-2.5 mb-6">
              {[
                'How AI is reshaping job markets in 2026',
                'Top 7 highest-paying AI careers (with salary ranges)',
                'Live demo of AI tools used by top professionals',
                'Live Q&A — Get your career questions answered'
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-200">
                  <span className="text-yellow-300 mt-1">✦</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            {/* Countdown */}
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-yellow-300" />
              <span className="text-sm text-gray-300">Next webinar in:</span>
              <span className="font-mono font-bold text-yellow-300" data-testid="masterclass-countdown">
                {d}d {h}h {m}m
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Users className="w-5 h-5 text-red-300" />
              <span className="text-red-200 font-bold">Only {seatsLeft} seats left</span>
              <span className="text-gray-400">&middot; Free Career Counseling included</span>
            </div>
          </div>

          {/* Right — form */}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-br from-yellow-400 via-orange-500 to-pink-500 rounded-3xl blur opacity-30"></div>
            <form
              onSubmit={submit}
              data-testid="masterclass-form"
              className="relative bg-black/70 backdrop-blur-xl border border-white/10 rounded-3xl p-7 md:p-9"
            >
              <h3 className="text-2xl font-black text-white mb-1">Reserve My Free Seat</h3>
              <p className="text-sm text-gray-400 mb-6">No payment. No spam. Industry-oriented training.</p>

              {status === 'ok' ? (
                <div className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 rounded-xl p-5 text-center" data-testid="masterclass-success">
                  <p className="font-bold text-lg mb-1">✓ You're In!</p>
                  <p className="text-sm">Our counselor will WhatsApp you the masterclass link shortly.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="masterclass-name"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-yellow-400"
                  />
                  <input
                    type="tel"
                    placeholder="Phone (with country code)"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    required
                    data-testid="masterclass-phone"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-yellow-400"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    data-testid="masterclass-email"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-yellow-400"
                  />
                  <select
                    value={form.course}
                    onChange={(e) => setForm({ ...form, course: e.target.value })}
                    data-testid="masterclass-course"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400"
                  >
                    {['AI Marketing', 'Performance Marketing', 'Film Making &amp; Editing', 'Game Development', 'Jewellery Design', 'Blender &amp; Character Design', 'Not Sure Yet'].map((c) => (
                      <option key={c} value={c.replace(/&amp;/g, '&')} className="bg-black">{c.replace(/&amp;/g, '&')}</option>
                    ))}
                  </select>

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    data-testid="masterclass-submit"
                    className="glow-cta w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-black py-4 rounded-xl text-base hover:scale-[1.02] transition-transform inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {status === 'loading' ? 'Reserving...' : 'Reserve My Seat'}
                    <ArrowRight className="w-5 h-5" />
                  </button>

                  {status === 'error' && (
                    <p className="text-red-400 text-sm text-center">Something went wrong. Please WhatsApp us instead.</p>
                  )}

                  <p className="text-[11px] text-gray-500 text-center pt-1">
                    <Calendar className="w-3 h-3 inline mr-1" /> Saturdays · 7pm IST · Industry-Oriented Training
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MasterclassFunnel;
